
import { Component, Vue, Emit } from 'vue-property-decorator';
import { SweetModal } from 'sweet-modal-vue';
import { translate } from '@/helpers/functions';
import stores from "@/stores";

type FilterGroup = Record<string | number, string>;

interface FilterStructure {
  stays: FilterGroup;
  "last-mood": FilterGroup;
  account: FilterGroup;
  guests: FilterGroup;
}

@Component({
  methods: { translate },
  components: { SweetModal },
})
export default class StaySearchFilter extends Vue {
  // Typed filter object
  filter: FilterStructure = {
    stays: { active: 'active', upcoming: 'upcoming', past: 'past' },
    "last-mood": { 1: 'upset', 2: 'neutral', 3: 'happy', 4: 'excited' },
    account: { default: 'default-account', advanced: 'advance-account', all: 'all-inclusive-account' },
    guests: { main: 'main-guest', guest: 'guest', staff: 'staff' },
  };

  selectedStatus: Record<string, boolean> = {};
  checkinRange = { startDate: null as string | null, endDate: null as string | null };
  company = '';
  country = '';

  mounted() {
    // Initialize selectedStatus keys
    (Object.keys(this.filter) as (keyof FilterStructure)[]).forEach(groupKey => {
      const group = this.filter[groupKey];
      Object.keys(group).forEach(key => {
        this.$set(this.selectedStatus, `${groupKey}-${key}`, false);
      });
    });

    // Load dynamic accounts if needed
    this.loadAccounts();

    // Listen to root event for clearing filters
    this.$root.$on("clear-filters", this.onClear);
  }

  beforeDestroy() {
    this.$root.$off("clear-filters", this.onClear);
  }

  loadAccounts() {
    stores.accounts.fetchData(1, '', 999999)
      .then((response: any) => {
        // Safely overwrite account group
        const accountGroup: FilterGroup = response.data.reduce((acc: FilterGroup, item: any) => {
          acc[item.id] = item.name;
          return acc;
        }, {});

        this.$set(this.filter, 'account', accountGroup);

        // Initialize new account keys in selectedStatus
        response.data.forEach((item: any) => {
          const key = `account-${item.id}`;
          if (!(key in this.selectedStatus)) {
            this.$set(this.selectedStatus, key, false);
          }
        });
      });
  }

  @Emit('apply-filter')
  emitFilter(payload: any) {
    return payload;
  }

  onSubmit() {
    const attributes: Record<string, any> = {};
    const queryParts: string[] = [];

    (Object.keys(this.filter) as (keyof FilterStructure)[]).forEach(groupKey => {
      const group = this.filter[groupKey];
      const selectedKeys = Object.keys(group).filter(k => this.selectedStatus[`${groupKey}-${k}`]);

      if (selectedKeys.length) {
        attributes[groupKey] = selectedKeys.map((k) => ({
          value: group[k],
          translatable: true
        }));

        const paramNameMap: Record<keyof FilterStructure, string> = {
          stays: 'stays[]',
          "last-mood": 'mood[]',
          account: 'account[]',
          guests: 'guestType[]',
        };

        const paramName = paramNameMap[groupKey] || groupKey;

        queryParts.push(
          selectedKeys
            .map(k =>
              groupKey === "last-mood" || groupKey === "account"
                ? `${paramName}=${Number(k)}`
                : `${paramName}=${k}`
            )
            .join('&')
        );
      }
    });

    if (this.company) {
      attributes.company = [{ value: this.company, translatable: false }];
      queryParts.push(`company=${encodeURIComponent(this.company)}`);
    }

    if (this.country) {
      attributes.country = [{ value: this.country, translatable: false }];
      queryParts.push(`country=${encodeURIComponent(this.country)}`);
    }

    if (this.checkinRange.startDate) {
      attributes["date-from"] = [{ value: this.checkinRange.startDate, translatable: false }];
      queryParts.push(`checkin_start=${this.formatDate(this.checkinRange.startDate)}`);
    }

    if (this.checkinRange.endDate) {
      attributes["date-to"] = [{ value: this.checkinRange.endDate, translatable: false }];
      queryParts.push(`checkin_end=${this.formatDate(this.checkinRange.endDate)}`);
    }

    const queryString = queryParts.join('&');
    this.emitFilter({ query: queryString, attributes });
    this.cancel();
  }

  formatDate(date: string | Date): string {
    const d = new Date(date);
    return d.toISOString().split('T')[0];
  }

  /** Reset all filters */
  onClear() {
    Object.keys(this.selectedStatus).forEach(k => (this.selectedStatus[k] = false));
    this.checkinRange = { startDate: null, endDate: null };
    this.company = '';
    this.country = '';
    this.emitFilter({ query: '', attributes: {} });
  }

  cancel() {
    // @ts-ignore - SweetModal ref not typed
    this.$refs.filter.close();
  }
}
